import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { ExperienceService } from '../services/experience.service'
import type { UiExperienceEvent, UiExperienceState } from '../types/experience.types'
import { toAppPath } from '@/utils/app-url'

/** Classic (Vue 2) landing page, used whenever the server can't map a page. */
export const CLASSIC_HOME = '/V2'

// A 200 that isn't the expected payload (e.g. an HTML fallback page) counts as a
// failed load, so the gate stays fail-open instead of reading `undefined` as "not eligible".
function isUiExperienceState(value: unknown): value is UiExperienceState {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as UiExperienceState).IsEligible === 'boolean'
  )
}

/**
 * New-UI rollout state for the current user. Loaded once by the router's
 * experience guard; only the "V3 session start already recorded" flag is
 * persisted, per browser tab (sessionStorage).
 */
export const useExperienceStore = defineStore(
  'experience',
  () => {
    const service = new ExperienceService()
    const state = ref<UiExperienceState | null>(null)
    const loadFailed = ref(false)
    const sessionStartTracked = ref(false)
    let loadPromise: Promise<void> | null = null

    // Fail open (decided): only a SUCCESSFUL response without ENABLE_VUE3_NEW_UI blocks.
    // There is no staff/admin/impersonation bypass — the datapoint alone grants access.
    const isBlocked = computed(() => !!state.value && !state.value.IsEligible)

    /** Loads the state once; concurrent callers share the in-flight request. Never rejects. */
    function ensureLoaded(): Promise<void> {
      if (state.value || loadFailed.value) return Promise.resolve()
      loadPromise ??= service
        .getState()
        .then((s) => {
          if (isUiExperienceState(s)) state.value = s
          else loadFailed.value = true
        })
        .catch(() => {
          // No retry this session: a failing endpoint must never lock users out.
          loadFailed.value = true
        })
        .finally(() => {
          loadPromise = null
        })
      return loadPromise
    }

    /** Records an event and returns the server-mapped redirect. Never throws: tracking must not break navigation. */
    async function track(event: UiExperienceEvent, fromPath: string): Promise<string | null> {
      try {
        return (await service.track(event, fromPath)).RedirectUrl ?? null
      } catch {
        return null
      }
    }

    function trackSessionStartOnce(): void {
      if (sessionStartTracked.value) return
      sessionStartTracked.value = true
      void track('V3SessionStart', window.location.pathname)
    }

    /** Records the switch back and leaves the SPA for the equivalent classic page. */
    async function switchToClassic(): Promise<void> {
      const url = await track('SwitchToV2', window.location.pathname)
      window.location.assign(url ?? CLASSIC_HOME)
    }

    /**
     * Sends a user without new-UI access (ENABLE_VUE3_NEW_UI off) straight to the equivalent
     * classic (V2) page, the same destination as the beta screen's "Back to TAMI" button. While
     * the new UI is still in private testing we redirect blocked users to V2 rather than showing
     * them the "private beta" screen. Records BlockedOnV3 so the server can map the intercepted
     * page to its V2 URL; falls back to the classic home.
     *
     * @param fromPath the in-app router path the gate intercepted (e.g. `to.fullPath`).
     */
    async function redirectBlockedToClassic(fromPath: string): Promise<void> {
      const url = await track('BlockedOnV3', toAppPath(fromPath))
      window.location.assign(url ?? CLASSIC_HOME)
    }

    function $reset(): void {
      state.value = null
      loadFailed.value = false
      sessionStartTracked.value = false
      loadPromise = null
    }

    return {
      state,
      loadFailed,
      sessionStartTracked,
      isBlocked,
      ensureLoaded,
      track,
      trackSessionStartOnce,
      switchToClassic,
      redirectBlockedToClassic,
      $reset,
    }
  },
  {
    persist: { storage: sessionStorage, pick: ['sessionStartTracked'] },
  },
)
